// PowerTranz guide-owned merchant accounts (DS-4717). Values mirror the API contract in
// "PowerTranz Guide-Owned Accounts: Technical Specification v4" — keep them in sync with the API.

export const PAYMENT_PROVIDERS = {
  STRIPE: "stripe",
  PAYPAL: "paypal",
  POWERTRANZ: "powertranz"
}

// Providers whose checkout submits itself (own button / hosted page), so the wizard's footer
// Submit — which drives the Stripe card flow — must be hidden.
export const SELF_SUBMITTING_PROVIDERS = [PAYMENT_PROVIDERS.PAYPAL, PAYMENT_PROVIDERS.POWERTRANZ]

// Connect validation, matching the API's Powertranz::ConnectContract.
export const PAGE_SET_PREFIX = "PTZ/"
export const PAGE_SET_FORMAT = /^PTZ\/\S+$/
export const MAX_CREDENTIAL_LENGTH = 255

export const CONNECTION_STATUS = {
  PENDING_VERIFICATION: "pending_verification",
  ACTIVE: "active",
  INVALID: "invalid",
  DISCONNECTED: "disconnected"
}

// Statuses for which a merchant account record exists (credentials saved).
export const ACCOUNT_STATUSES = [CONNECTION_STATUS.ACTIVE, CONNECTION_STATUS.PENDING_VERIFICATION, CONNECTION_STATUS.INVALID]

// Normalized result of a connect or Verify Connection call, shown to the guide.
export const VERIFICATION_OUTCOMES = {
  VERIFIED: "verified",
  // Saved, but the API hasn't confirmed the credentials yet.
  PENDING: "pending",
  FAILED: "failed",
  INVALID_CREDENTIALS: "invalid_credentials",
  HOSTED_PAGE_INVALID: "hosted_page_invalid",
  PROVIDER_UNAVAILABLE: "provider_unavailable",
  RETRIABLE: "retriable"
}

export const BILLING_STANDING = {
  IN_GOOD_STANDING: "in_good_standing",
  SUSPENDED: "suspended"
}

// Attempt states returned by GET /powertranz/status.
export const ATTEMPT_STATES = {
  CREATED: "created",
  AUTHENTICATION_PENDING: "authentication_pending",
  COMPLETION_PENDING: "completion_pending",
  PROCESSING: "processing",
  UNKNOWN: "unknown",
  CONFIRMED: "confirmed",
  FAILED: "failed",
  EXPIRED: "expired"
}

// The outcome is still being settled by the API (completion call in flight or reconciliation).
// The guest must wait — never retry — while an attempt is in one of these.
export const PENDING_ATTEMPT_STATES = [
  ATTEMPT_STATES.CREATED,
  ATTEMPT_STATES.AUTHENTICATION_PENDING,
  ATTEMPT_STATES.COMPLETION_PENDING,
  ATTEMPT_STATES.PROCESSING,
  ATTEMPT_STATES.UNKNOWN
]

export const TERMINAL_FAILURE_STATES = [ATTEMPT_STATES.FAILED, ATTEMPT_STATES.EXPIRED]

// Integration guide 5.5: the guest is still on the hosted page (no card submitted yet)…
export const HOSTED_PAGE_STATES = [ATTEMPT_STATES.CREATED, ATTEMPT_STATES.AUTHENTICATION_PENDING]
// …or the card was submitted and the API is confirming it: never offer to pay again.
export const CONFIRMING_STATES = [ATTEMPT_STATES.COMPLETION_PENDING, ATTEMPT_STATES.PROCESSING, ATTEMPT_STATES.UNKNOWN]

// The API's redirect page posts this to the parent window when the hosted page finishes.
export const RESULT_MESSAGE_TYPE = "powertranz:result"

// Stop polling a payment still unconfirmed after this long (integration guide 5.5).
export const STATUS_POLL_MAX_DURATION_MS = 10 * 60 * 1000

// Machine-readable error codes (spec "Error codes"). Messages live under powertranz.errors.*.
export const ERROR_CODES = {
  INVALID_CREDENTIALS: "invalid_credentials",
  HOSTED_PAGE_NOT_FOUND: "hosted_page_not_found",
  VALIDATION_FAILED: "validation_failed",
  CREDENTIALS_UNAVAILABLE: "credentials_unavailable",
  NOT_CONNECTED: "not_connected",
  NOT_A_GUIDE: "not_a_guide",
  MERCHANT_UNAVAILABLE: "merchant_unavailable",
  SLOT_RELEASED: "slot_released",
  INVALID_PROMO_CODE: "invalid_promo_code",
  UNKNOWN_TOKEN: "unknown_token",
  BOOKING_NOT_FOUND: "booking_not_found",
  ATTEMPT_NOT_FOUND: "attempt_not_found",
  CHECKOUT_CLOSED: "checkout_closed",
  SLOT_TAKEN: "slot_taken",
  ALREADY_PAID: "already_paid",
  AMOUNT_CHANGED: "amount_changed",
  PAYMENT_IN_PROGRESS: "payment_in_progress",
  PROCESSING: "processing",
  UNKNOWN: "unknown",
  RECONCILIATION_REQUIRED: "reconciliation_required",
  DECLINED: "declined",
  AUTHENTICATION_FAILED: "authentication_failed",
  FRAUD_DECLINED: "fraud_declined",
  EXPIRED: "expired",
  NON_3DS_NOT_ALLOWED: "non_3ds_not_allowed",
  NO_SAVED_CARD: "no_saved_card",
  SAVED_CARD_DECLINED: "saved_card_declined",
  OVER_TOKEN_LIMIT: "over_token_limit",
  GATEWAY_UNAVAILABLE: "gateway_unavailable",
  REFUND_EXCEEDS_AVAILABLE: "refund_exceeds_available",
  REFUND_MANUAL_REVIEW: "refund_manual_review",
  NOT_IN_PILOT: "not_in_pilot",
  LINK_INVALID: "link_invalid",
  SAVED_CARDS_DISABLED: "saved_cards_disabled",
  SAVED_CARD_NOT_FOUND: "saved_card_not_found",
  INCOME_NOT_FOUND: "income_not_found",
  INVALID_REFUND_AMOUNT: "invalid_refund_amount"
}

// 202 outcomes: the charge may have happened; poll status, never retry.
export const PENDING_ERROR_CODES = [ERROR_CODES.PROCESSING, ERROR_CODES.UNKNOWN, ERROR_CODES.RECONCILIATION_REQUIRED]

// Saved-card charge not possible: the guide should send a payment link instead.
// The checkout can't be retried after these: the booking or link is done with, so no "Start again".
export const FINAL_CHECKOUT_ERROR_CODES = [
  ERROR_CODES.CHECKOUT_CLOSED,
  ERROR_CODES.ALREADY_PAID,
  ERROR_CODES.LINK_INVALID,
  ERROR_CODES.SLOT_TAKEN,
  ERROR_CODES.BOOKING_NOT_FOUND,
  ERROR_CODES.NOT_IN_PILOT
]

export const SEND_LINK_ERROR_CODES = [
  ERROR_CODES.NO_SAVED_CARD,
  ERROR_CODES.SAVED_CARD_DECLINED,
  ERROR_CODES.OVER_TOKEN_LIMIT,
  ERROR_CODES.SAVED_CARDS_DISABLED
]

// Result page polling (spec "Frontend contract" 3): every 3 s for 60 s, then every 15 s.
export const STATUS_POLL_FAST_INTERVAL_MS = 3000
export const STATUS_POLL_FAST_DURATION_MS = 60000
export const STATUS_POLL_SLOW_INTERVAL_MS = 15000

// The hosted page posts its result to the API, which navigates the parent (iframe) or opener
// (popup) to the result page. Window name lets a retry reuse the same popup.
export const HOSTED_PAGE_POPUP_NAME = "powertranz_checkout"
export const HOSTED_PAGE_POPUP_FEATURES = "width=500,height=720"
// The API's hosted-page window (POWERTRANZ_SPI_TTL_SECONDS default). Only used when the device clock
// is so far off that the API's expires_at already looks past.
export const HOSTED_PAGE_SESSION_SECONDS = 300
// Highest processing fee (percent) a guide can pass on with PowerTranz charges; the API checks it too.
export const POWERTRANZ_CARD_FEE_MAX_PERCENT = 10
// How often the widget checks whether the guest closed the payment popup.
export const POPUP_CLOSED_CHECK_MS = 1000
// How many times (one per POPUP_CLOSED_CHECK_MS) the checkout retries closing its popup when it goes away.
export const POPUP_CLOSE_ATTEMPTS = 15

// srcdoc frames start with the embedding page's origin. allow-same-origin is needed so the
// PowerTranz page and the issuer's 3DS challenge keep their own cookies after the hosted form
// navigates the frame; allow-top-navigation lets the API's result page leave the iframe.
export const HOSTED_PAGE_IFRAME_SANDBOX = "allow-scripts allow-forms allow-same-origin allow-top-navigation allow-popups"
